import { useMemo, useCallback } from "react";
import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect.mjs";
const animationTimeout = 300;
const entranceTransition = "transform 0.2s ease, opacity 0.2s ease";
const exitTransition = "opacity 0.1s ease";
const animate = (element, transforms, transition, done) => {
  const fallbackTimeout = setTimeout(() => {
    if (done) {
      done();
    }
  }, animationTimeout);
  transforms.forEach(({ property, from = "" }) => {
    element.style.setProperty(property, from);
  });
  element.style.setProperty("transition", "");
  const transitionEndHandler = (ev) => {
    if (ev.target !== element) {
      return;
    }
    element.style.setProperty("transition", "");
    if (done) {
      done();
    }
    element.removeEventListener("transitionend", transitionEndHandler);
    clearTimeout(fallbackTimeout);
  };
  element.addEventListener("transitionend", transitionEndHandler);
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      element.style.setProperty("transition", transition);
      transforms.forEach(({ property, to = "" }) => {
        element.style.setProperty(property, to);
      });
    });
  });
};
const useFlipList = () => {
  const refs = useMemo(() => /* @__PURE__ */ new Map(), []);
  const positions = useMemo(() => /* @__PURE__ */ new Map(), []);
  useIsomorphicLayoutEffect(() => {
    const animations = [];
    Array.from(refs.entries()).forEach(([toastKey, element]) => {
      if (element) {
        const prevTop = positions.get(toastKey);
        const { top, height } = element.getBoundingClientRect();
        if (typeof prevTop === "number" && prevTop !== top) {
          animations.push({
            element,
            transition: entranceTransition,
            transforms: [
              {
                property: "transform",
                from: `translateY(${prevTop - top}px)`
              }
            ]
          });
        } else if (typeof prevTop !== "number") {
          animations.push({
            element,
            transition: entranceTransition,
            transforms: [
              {
                property: "transform",
                from: `translateY(${height}px)`
              },
              {
                property: "opacity",
                from: "0"
              }
            ]
          });
        }
        positions.set(toastKey, element.getBoundingClientRect().top);
      } else {
        refs.delete(toastKey);
      }
    });
    animations.forEach(({ element, transforms, transition }) => {
      animate(element, transforms, transition);
    });
  });
  const remove = useCallback(
    (toastKey, cb) => {
      const element = refs.get(toastKey);
      if (element) {
        animate(
          element,
          [
            {
              property: "opacity",
              to: "0"
            }
          ],
          exitTransition,
          cb
        );
      }
    },
    [refs]
  );
  const itemRef = useCallback(
    (toastKey) => (ref) => {
      refs.set(toastKey, ref);
    },
    [refs]
  );
  return {
    itemRef,
    remove
  };
};
export {
  useFlipList
};
